import {
  DerivativesClosedPositionProjection,
  DerivativesGroupDto,
  DerivativesOhlcSignalDto,
  DerivativesOpenedPositionProjection,
  DerivativesOrderProjection,
  FavoriteDerivativesDto,
  TradingPairContractType,
  TradingPairStatus,
  TradingPairWithStatsDto,
} from 'common/api/autogenerated/trading-gateway';
import { DerivativesChartsDto } from 'common/api/autogenerated/trading-gateway/models/DerivativesChartsDto';

import { decimalAddition, localizeNumber } from 'common/helpers';
import { AssetDtoEnhanced, AssetsHashMap, GroupsHashMap } from 'common/types';

import { ChartDataCandle } from 'components/ChartWidget/types';
import { OrderBookDto, OrderBookSnapshotDto } from 'components/OrderBook/types';

import { revertNumber } from 'utils/revertNumber';

import { INVERSED_PNL_DIGITS, LINEAR_PNL_DIGITS } from '../constants';
import { FuturePosition } from './derivatives.initialState';

export const mapDerivativesGroupsFromServer = (data: DerivativesGroupDto[]): GroupsHashMap =>
  data.reduce<GroupsHashMap>((result, item) => {
    if (item.type) {
      return {
        ...result,
        [item.type]: {
          type: item.type,
          symbols: item.codes || [],
        },
      };
    }
    return result;
  }, {});

export const mapDerivativesFromServer = (
  stateDerivatives: AssetsHashMap,
  data: TradingPairWithStatsDto[],
): AssetsHashMap =>
  data.reduce<AssetsHashMap>((result, item) => {
    if (item.symbol && item.code) {
      const {
        symbol,
        lastTradePrice,
        bestAskPrice,
        bestBidPrice,
        status,
        code,
        tradeVolumeUsd,
        settlementCurrency,
        maxLeverage,
        decimalPlaces,
        openPrice,
        highPrice,
        lowPrice,
        description,
        contractType,
        contractValue,
      } = item;

      const asset: AssetDtoEnhanced = {
        ...item,
        symbol,
        code,
        name: symbol,
        displayName: symbol,
        currency: settlementCurrency || '',
        lastTradePrice,
        bestAskPrice,
        bestBidPrice,
        trend: stateDerivatives[code]?.trend || [],
        bid: bestBidPrice,
        ask: bestAskPrice,
        price: lastTradePrice || 0,
        prevPrice: lastTradePrice || 0,
        prevAsk: bestAskPrice || 0,
        prevBid: bestBidPrice || 0,
        isInternal: status === TradingPairStatus.DISABLED || status === TradingPairStatus.HIDDEN,
        dailyTradeVolumeUsd: tradeVolumeUsd,
        maxLeverage,
        digits: decimalPlaces,
        openPrice,
        contractType,
        contractValue,
        dayChange: {
          first: openPrice,
          high: highPrice,
          low: lowPrice,
        },
        description,
        mode: 'perpetual',
      };

      if (code) {
        return {
          ...result,
          [code]: {
            ...stateDerivatives[code],
            ...asset,
          },
        };
      }

      return {
        ...result,
        [symbol]: {
          ...stateDerivatives[symbol],
          ...asset,
        },
      };
    }
    return result;
  }, {});

export const mapFavoritesFromServer = (data: FavoriteDerivativesDto['data']) => {
  if (!data) return [];
  return data.map(({ code }) => decodeURIComponent(String(code)));
};

export const mapDerivativeCandlesFromServer = (data: DerivativesChartsDto['data'] = []): ChartDataCandle[] =>
  (data || []).reverse().map((candle) => {
    const { open = 0, high = 0, low = 0, close = 0, time = 0 } = candle;

    return { open, high, low, close, time: new Date(time || 0).getTime() };
  });
export const applyTrendToDerivative = (trends: DerivativesOhlcSignalDto[], derivativesHashMap: AssetsHashMap) => {
  const trendsHashMap = trends.reduce<Record<string, DerivativesOhlcSignalDto[]>>((acc, val) => {
    if (!val.tradingPairCode) return acc;
    acc[val.tradingPairCode] = acc[val.tradingPairCode] ? acc[val.tradingPairCode].concat(val) : [val];
    return acc;
  }, {});

  Object.keys(trendsHashMap).forEach((code) => {
    if (derivativesHashMap[code])
      derivativesHashMap[code].trend = trendsHashMap[code].map((i) => i.close || 0).reverse();
  });

  return derivativesHashMap;
};

export const mapDerivativeOrderBookSnapshot = (
  snapshot: OrderBookSnapshotDto,
  asset: AssetDtoEnhanced,
): OrderBookDto => {
  const { version, asks = [], bids = [] } = snapshot;
  const { contractType, contractValue, price: lastPrice } = { ...asset };

  const buyMap = new Map<number, number>();
  const sellMap = new Map<number, number>();

  const formatAmount = (initialAmount: number) => {
    if (!contractType || !contractValue) return initialAmount;

    if (contractType === TradingPairContractType.LINEAR) {
      return initialAmount * contractValue;
    }
    if (contractType === TradingPairContractType.INVERSE) return (initialAmount * contractValue) / lastPrice;

    return initialAmount;
  };

  if (bids) {
    bids.forEach((row) => {
      const [price, amount] = row;

      buyMap.set(price, formatAmount(amount));
    });
  }

  if (asks) {
    asks.forEach((row) => {
      const [price, amount] = row;

      sellMap.set(price, formatAmount(amount));
    });
  }

  const prevVersion = version - 1;

  return {
    prevVersion,
    version,
    buy: new Map<number, number>(Array.from(buyMap).sort((left, right) => right[0] - left[0])),
    sell: new Map<number, number>(Array.from(sellMap).sort((left, right) => left[0] - right[0])),
  };
};

export const mapDerivativeOrderBookUpdate = (
  state: OrderBookDto,
  update: OrderBookSnapshotDto,
  asset: AssetDtoEnhanced,
): OrderBookDto => {
  const { buy, sell, version: prevVersion } = { ...state };
  const { version, asks = [], bids = [] } = { ...update };
  const { contractType, contractValue, price: lastPrice } = asset;

  const formatAmount = (initialAmount: number) => {
    if (!contractType || !contractValue) return initialAmount;

    if (contractType === TradingPairContractType.LINEAR) return initialAmount * contractValue;
    if (contractType === TradingPairContractType.INVERSE) return (initialAmount * contractValue) / lastPrice;

    return initialAmount;
  };

  const buyMap = new Map<number, number>(buy);
  const sellMap = new Map<number, number>(sell);

  bids.forEach((row) => {
    const [price, amount] = row;

    if (!amount) buyMap.delete(price);
    else buyMap.set(price, formatAmount(amount));
  });

  asks.forEach((row) => {
    const [price, amount] = row;

    if (!amount) sellMap.delete(price);
    else sellMap.set(price, formatAmount(amount));
  });

  return {
    prevVersion,
    version,
    buy: new Map<number, number>(Array.from(buyMap).sort((left, right) => right[0] - left[0])),
    sell: new Map<number, number>(Array.from(sellMap).sort((left, right) => left[0] - right[0])),
  };
};

export const mapOpenPositionsFromServer = (position: DerivativesOpenedPositionProjection): FuturePosition => {
  const {
    // 85523 TEMP CODE (markPrice -> lastPrice)
    lastPriceUnrealizedPnL = 0,
    lastPriceUnrealizedPnLRate = 0,
    tradingPairCode = '',
    settlementCurrency = '',
    tradingFee: _tradingFee = 0,
    fundingFee: _fundingFee = 0,
    liquidationPenalty: _liquidationPenalty = 0,
  } = position;

  const [baseCurrency] = String(tradingPairCode).split('-');

  const digitsForPnl = baseCurrency === settlementCurrency ? INVERSED_PNL_DIGITS : LINEAR_PNL_DIGITS;

  const tradingFee = revertNumber(_tradingFee);
  const fundingFee = revertNumber(_fundingFee);
  const liquidationPenalty = revertNumber(_liquidationPenalty);

  const fee = decimalAddition(fundingFee ?? 0, tradingFee ?? 0)
    .add(liquidationPenalty ?? 0)
    .toNumber();

  const pnl = decimalAddition(lastPriceUnrealizedPnL, fee).toNumber();
  const pnlSign = pnl < 0 ? '-' : '+';

  const pnlFormated = `${pnlSign}${localizeNumber(pnl, {
    minDigits: digitsForPnl,
  })}`;

  const pnlPercentage = lastPriceUnrealizedPnLRate * 100;
  const pnlPercentageFormated = `${pnlSign}${localizeNumber(pnlPercentage, {
    minDigits: 0,
    maxDigits: 2,
  })}%`;

  return {
    ...position,
    tradingFee,
    fundingFee,
    liquidationPenalty,
    pnl,
    pnlFormated,
    pnlPercentage,
    pnlPercentageFormated,
  };
};

export const mapOpenedPositionsFromServer = (positions: DerivativesOpenedPositionProjection[]): FuturePosition[] =>
  positions.map(mapOpenPositionsFromServer);

export const mapClosedPositionFromServer = (
  position: DerivativesClosedPositionProjection,
): DerivativesClosedPositionProjection => ({
  ...position,
  tradingFee: revertNumber(position.tradingFee),
  fundingFee: revertNumber(position.fundingFee),
  liquidationPenalty: revertNumber(position.liquidationPenalty),
});

export const mapClosedPositionsFromServer = (
  positions: DerivativesClosedPositionProjection[],
): DerivativesClosedPositionProjection[] => positions.map(mapClosedPositionFromServer);

export const mapFuturesOrdersHistoryFromServer = (
  orders: DerivativesOrderProjection[],
): DerivativesOrderProjection[] => {
  const uniqueOrdersObject: Record<number, DerivativesOrderProjection> = {};

  orders.forEach((order) => {
    if (order.id) uniqueOrdersObject[order.id] = order;
  });

  return Object.values(uniqueOrdersObject);
};
